import { isRouteErrorResponse, useRouteError, Link as RouterLink } from 'react-router-dom';

// material-ui
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// project imports
import CenteredStatusCard from 'components/common/CenteredStatusCard';
import { APP_DEFAULT_PATH } from 'config';

/**
 * Handles loader/action/render errors from React Router (including 404 when no route matches).
 */
export default function RouteErrorBoundary() {
  const error = useRouteError();

  // eslint-disable-next-line no-console
  console.error('[RouteErrorBoundary]', error);

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return (
        <FullScreenStatus
          code="404"
          title="Page not found"
          detail={typeof error.data === 'string' ? error.data : 'No route matched this URL.'}
        />
      );
    }
    if (error.status === 401 || error.status === 403) {
      return (
        <FullScreenStatus
          code={String(error.status)}
          title="Not authorized"
          detail={typeof error.data === 'string' ? error.data : 'You cannot access this resource.'}
        />
      );
    }
    return (
      <FullScreenStatus
        code={String(error.status)}
        title="Something went wrong"
        detail={typeof error.data === 'string' ? error.data : error.statusText || 'Unexpected response.'}
      />
    );
  }

  const message = error instanceof Error ? error.message : String(error);
  return (
    <FullScreenStatus
      code="Error"
      title="Application error"
      detail={message}
      isError
    />
  );
}

function FullScreenStatus({ code, title, detail, isError }) {
  return (
    <CenteredStatusCard maxWidth={420} sx={{ bgcolor: 'background.default' }}>
      <Stack spacing={2} sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="h2" color={isError ? 'error' : 'text.secondary'} sx={{ fontWeight: 700 }}>
          {code}
        </Typography>
        <Typography variant="h5">{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
          {detail}
        </Typography>
        <Button component={RouterLink} to={APP_DEFAULT_PATH} variant="contained" sx={{ alignSelf: 'center' }}>
          Go to dashboard
        </Button>
      </Stack>
    </CenteredStatusCard>
  );
}
